import store from '@/store';
import axiosInstance from '@/axios/instance';
import apiSettings from '@/config/api';
import { prepareSignaturePayload } from '@/utils/dataAdapter';

/**
 * Build an absolute backend URL. Paths are absolute ("/api/…/") and the configured
 * root URL is an origin, so the root falls back to the page origin when unset.
 */
const backendUrl = (path, query = {}) => {
    const url = new URL(path, apiSettings.backend.rootUrl || window.location.origin);
    Object.entries(query)
        .filter(([, value]) => value !== undefined)
        .forEach(([key, value]) => url.searchParams.set(key, value));
    return url.href;
};

/**
 * Decode JWT token.
 */
const decodeJwt = () => {
    const {
        jwtToken,
    } = store.state.settings;

    return axiosInstance.get(
        backendUrl('/api/jwt-decode/', { token: jwtToken }),
    );
};

/**
 * Get contract data from backend. Shared across all contract types.
 */
const getContractData = () => {
    const {
        signature,
        url,
        jwtToken,
    } = store.state.settings;

    return axiosInstance.get(
        backendUrl(url, { signature }),
        {
            headers: {
                Authorization: `JWT ${jwtToken}`,
            },
        },
    );
};

/**
 * Sign contract, passing the necessary information to the backend if any.
 * Shared across all contract types.
 */
const signContract = () => {
    const {
        url,
        jwtToken,
        mode,
    } = store.state.settings;

    /**
     * TODO: Temporary, until backend aligns the verbs to something common to all contract types.
     * See: https://thermondo.atlassian.net/browse/BE-717
     */
    const settings = {
        customerOffer: {
            method: 'post',
            urlSuffix: 'order/',
        },
        contracting: {
            method: 'post',
            urlSuffix: 'submit/',
        },
        maintenanceOffer: {
            method: 'put',
            urlSuffix: '',
        },
    };

    // `url` comes from the JWT and always ends with "/" (DRF router detail route).
    return axiosInstance[settings[mode].method](
        backendUrl(`${url}${settings[mode].urlSuffix}`),
        prepareSignaturePayload(),
        {
            headers: {
                Authorization: `JWT ${jwtToken}`,
            },
        },
    );
};

/**
 * Validate an IBAN value against the backend.
 */
const validateIbanCondition = () => store.state.collectedData[store.state.settings.mode]
    .bankAccountIban;
const validateIban = () => {
    const {
        jwtToken,
    } = store.state.settings;

    return axiosInstance.get(
        backendUrl('/api/validate-iban/', {
            iban: store.state.collectedData[store.state.settings.mode].bankAccountIban,
        }),
        {
            headers: {
                Authorization: `JWT ${jwtToken}`,
            },
        },
    );
};

export {
    decodeJwt,
    getContractData,
    signContract,
    validateIban,
    validateIbanCondition,
};
